import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Upload } from "antd";
import { find, isNil } from "lodash";
import { aiccLessonReviewScriptApi } from "services/aiccLearningAssets";
import { AiccLessonReviewScript } from "types/model/aiccLearningAssets";
import AgenticAssetLink from "../AgenticAssetLink";
import LearningAssetPage from "../LearningAssetPage";
import LessonEntryFilters from "../LessonEntryFilters";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import { useLessonEntryFilters } from "../hooks/useLessonEntryFilters";
import { useLessonEntryPagination } from "../hooks/useLessonEntryPagination";
import { useAssetImport } from "../hooks/useAssetImport";
import getColumns from "./columnConfig";
import ScriptDetailDrawer from "./ScriptDetailDrawer";
import UploadPreviewModal from "./UploadPreviewModal";
import styles from "./LessonReviewScript.module.scss";

const LESSON_REVIEW_SCRIPT_TITLE = "Lesson Review Script";

const FILE_ACCEPT = ".zip";

function LessonReviewScript() {
  const state = useLearningAssetVersions(aiccLessonReviewScriptApi.fetchVersions);
  const {
    languageId,
    items,
    filters: lessonFilters,
    groupStatus,
    isEditable,
    isDraft,
    isLoading,
    isSaving,
    runWrite,
  } = state;

  const {
    entries,
    levelOptions,
    lessonOptions,
    selectedLevels,
    selectedLessons,
    setSelectedLevels,
    setSelectedLessons,
  } = useLessonEntryFilters(items, lessonFilters);

  const pagination = useLessonEntryPagination(entries, groupStatus);

  const { uploadFile, preview, isValidating, closeUpload, selectFile, confirmUpload } = useAssetImport({
    api: aiccLessonReviewScriptApi,
    languageId,
    isDraft,
    runWrite,
  });

  const isUploading = isValidating || !isNil(preview);
  const isActionDisabled = isNil(languageId) || isUploading;

  const [viewedId, setViewedId] = useState<string>();
  const viewedLesson = find(entries, { id: viewedId });

  const closeDetail = useCallback(() => setViewedId(undefined), []);

  useEffect(() => {
    closeDetail();
  }, [closeDetail, groupStatus, languageId]);

  const columns = useMemo(() => getColumns({ onView: (record) => setViewedId(record.id) }), []);

  return (
    <LearningAssetPage<AiccLessonReviewScript>
      title={LESSON_REVIEW_SCRIPT_TITLE}
      headerExtra={<AgenticAssetLink />}
      columns={columns}
      state={{ ...state, items: entries }}
      pagination={pagination}
      rowClassName={() => styles.clickableRow}
      onRow={(record) => ({ onClick: () => setViewedId(record.id) })}
      filters={
        <LessonEntryFilters
          levelOptions={levelOptions}
          lessonOptions={lessonOptions}
          selectedLevels={selectedLevels}
          selectedLessons={selectedLessons}
          setSelectedLevels={setSelectedLevels}
          setSelectedLessons={setSelectedLessons}
          disabled={isLoading}
        />
      }
      isLocked={isUploading}
      toolbarActions={
        isEditable && (
          <Upload accept={FILE_ACCEPT} showUploadList={false} beforeUpload={selectFile} disabled={isActionDisabled}>
            <Button type="primary" shape="round" loading={isValidating} disabled={isActionDisabled}>
              Upload
            </Button>
          </Upload>
        )
      }
    >
      <ScriptDetailDrawer lesson={viewedLesson} onClose={closeDetail} />
      <UploadPreviewModal
        visible={!isNil(preview)}
        fileName={uploadFile?.name}
        preview={preview}
        isSaving={isSaving}
        onCancel={closeUpload}
        onConfirm={confirmUpload}
      />
    </LearningAssetPage>
  );
}

export default LessonReviewScript;
